/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { AxiosError } from "axios";
import Button from "components/Button";
import Content from "components/Content";
import Form from "components/Form";
import Label from "components/Label";
import useAsync from "hooks/use-async";
import { useSession } from "hooks/use-session";
import { useRouter } from "next/router";
import Footer from "pages/components/Footer";
import Topbar from "pages/components/Topbar";
import React, { useEffect } from "react";
import toast from "react-hot-toast";
import { ExtendedNextPage } from "types/pages";
import { api } from "api/axios";
import {
  Place,
  required,
  formatErrors,
  formatHSErrors,
  HSPayload,
  parseAdrAddress,
  hasAddressAttirbute
} from "utils/form";
import { purgeObject } from "utils/object";
import styles from "./styles.module.scss";
import segment from "../../../lib/segment";

type FormValues = {
  email: string;
  firstname: string;
  lastname: string;
  phone: string;
  address: Place;
};

const LeadForm: ExtendedNextPage = () => {
  const { utm } = useSession();

  useEffect(() => {
    segment.track("LeadFormViewed", {});
  }, []);

  const formatPayload = (values: FormValues) => {
    const newValues: { [key: string]: any } = { ...values };

    const { firstname, lastname, phone, email } = values;
    const { address: place } = newValues;

    delete newValues.address;

    const { state, zip, city, country, address } = parseAdrAddress(
      place.adr_address
    );

    return {
      "0-2/country": country,
      "0-2/address": `${address} ${city} ${state} ${zip}`,
      "0-2/name": `${address} ${city} ${state} ${zip}`,
      "0-2/zip": zip,
      "0-2/city": city,
      "0-2/state": state,
      firstname,
      lastname,
      phone,
      email,
      form_variant: "lead_form",
      ...purgeObject(utm)
    } as { [key: string]: string };
  };

  const router = useRouter();

  const { query } = router;

  const { email, firstname, lastname, phone } = query;

  const initialValues = { email, firstname, lastname, phone };

  const [submit, submitting] = useAsync(async (values: FormValues) => {
    const newValues = formatPayload(values);

    segment.track("LeadFormSubmitted", newValues);

    await api
      .post(
        `https://api.hsforms.com/submissions/v3/integration/submit/20608861/7c39c5b7-e021-427d-a2c5-0f977689e826`,
        {
          fields: Object.keys(newValues).map((key) => ({
            name: key,
            value: newValues[key as keyof FormValues]
          }))
        }
      )
      .then(() => {
        toast.success(
          "Thank you! We got your info, we'll get back too you soon!",
          {
            position: "top-right"
          }
        );

        router.push("/start/form/calendar");
      })
      .catch((e) => {
        const payload = (e as AxiosError).response?.data;
        toast.error(
          payload
            ? formatHSErrors(payload as HSPayload)
            : "Something went wrong"
        );
      });
  });

  return (
    <Content
      containerClassName={styles.wrapper}
      contentClassName={styles.container}
    >
      <Label color="white" variant="h2-centered">
        Start humming
      </Label>
      <Form
        id="lead_form"
        initialValues={initialValues}
        className={styles.form}
        onSubmit={submit}
        onError={(errors) => {
          toast.error(formatErrors(errors), { position: "top-right" });
        }}
        fields={[
          {
            validate: [required("Email field is required")],
            id: "email",
            type: "text",
            title: "Email *",
            placeholder: "Please enter your email"
          },
          {
            validate: [required("First name field is required")],
            id: "firstname",
            type: "text",
            title: "First name *",
            placeholder: "Please enter your first name"
          },
          {
            validate: [required("Last name field is required")],
            id: "lastname",
            type: "text",
            title: "Last name *",
            placeholder: "Please enter your last name"
          },
          {
            validate: [required("Phone number field is required")],
            id: "phone",
            type: "text",
            title: "Phone number *",
            placeholder: "Please enter your phone number"
          },
          {
            validate: [
              required("Address field is required"),
              hasAddressAttirbute(["zip"], "Invalid address, missing zip code"),
              hasAddressAttirbute(["city"], "Invalid address, missing city"),
              hasAddressAttirbute(["state"], "Invalid address, missing state")
            ],
            id: "address",
            type: "location",
            title: "Home address *",
            placeholder: "Please enter your home address"
          },
          {
            title: "How did you hear about us? *",
            id: "how_customer_heard_about_us",
            type: "select",
            options: [
              { label: "Friend", value: "Referral" },
              {
                label: "Mailer",
                value: "Mailer"
              },
              {
                label: "Digital Ad",
                value: "Digital Ad"
              },
              {
                label: "Douglas Elliman Agent",
                value: "Douglas Elliman Agent"
              },
              {
                label: "Compass Agent",
                value: "Compass Agent"
              },
              {
                label: "Houlihan Lawrence Agent",
                value: "Houlihan Lawrence Agent"
              },
              {
                label: "BHHS Agent",
                value: "BHHS Agent"
              },
              {
                label: "Article",
                value: "Article"
              }
            ]
          }
        ]}
      >
        {() => (
          <Button
            type="submit"
            loading={submitting}
            className={styles.submit}
            variant="secondary"
          >
            Submit
          </Button>
        )}
      </Form>
    </Content>
  );
};

LeadForm.getLayout = (page) => {
  return (
    <div>
      <Topbar />
      {page}
      <Footer />
    </div>
  );
};

export default LeadForm;
